import {
  Box,
  Breadcrumbs,
  Grid,
  Link,
  Tab,
  Tabs,
  TabsProps,
  Typography,
} from "@mui/material";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Home as HomeIcon } from "@mui/icons-material";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { useCanGet } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/Get";
import { useCanListDiscoveryResults } from "~/lib/iam/actions/mondoo/integrations/IntegrationsManager/ListDiscoveryResults";
import { useCanAssign } from "~/lib/iam/actions/cnspec/policy/PolicyResolver/Assign";
import {
  ActionType,
  GetClientIntegrationQuery,
  GetIntegrationsSummaryDocument,
  useDeleteClientIntegrationMutation,
  useGetClientIntegrationQuery,
  useListDiscoveryResultsQuery,
  usePingIntegrationLazyQuery,
  useTriggerActionLazyQuery,
} from "~/operations";
import { AwsIntegrationHostedHeader } from "./AwsIntegrationHostedHeader";
import { useState } from "react";
import { IntegrationRecommendedPolicies } from "../../components/IntegrationRecommendedPolicies";
import { AWSOverviewAccountTab } from "~/components/aws-overview-account-tab";
import { AWSIntegrationStatus } from "../AwsIntegrationServerlessPage/AwsIntegrationServerlessPage";
import { useSnackbar } from "notistack";
import { ConfirmationDialog } from "~/components/ConfirmationDialog";
import { AwsIntegrationAssetsButton } from "../components/AwsIntegrationAssetsButton";
import { setDocumentTitle } from "~/lib/commonUtils";
import { INTEGRATION_MRN } from "~/pages/integrations/utils/integrationConstants";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";
import { SpacePagesIntegrations } from "../../utils/integrationRoutesUtils";

export type ClientIntegration = GetClientIntegrationQuery["clientIntegration"];
export type Integration = ClientIntegration["integration"];
export type ConfigurationOptions = NonNullable<
  Integration["configurationOptions"]
>;
export type AwsConfigurationOptions = Extract<
  ConfigurationOptions,
  { __typename: "HostedAwsConfigurationOptions" }
>;
export type AwsIntegration = Integration & {
  configurationOptions: AwsConfigurationOptions;
};

type AwsIntegrationServerlessPageState = {
  currentTab: string;
  openDeleteConfirmDialog: boolean;
  isDeleting: boolean;
  isEditing: boolean;
  lastUpdate: number;
};

interface AwsIntegrationHostedPageProps {
  scope: SpaceScope | OrganizationScope;
}

export function AwsIntegrationHostedPage({
  scope,
}: AwsIntegrationHostedPageProps) {
  const navigate = useNavigate();
  const { integrationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const defaultTab = "overview";
  const tab = window.location.pathname.split("/").pop() || defaultTab;
  const currentTab = ["overview", "policies", "configuration"].includes(tab)
    ? tab
    : defaultTab;

  const [state, setState] = useState<AwsIntegrationServerlessPageState>({
    currentTab,
    openDeleteConfirmDialog: false,
    isDeleting: false,
    isEditing: false,
    lastUpdate: Date.now(),
  });

  function mergeState(params: any) {
    return setState((prevState) => ({
      ...prevState,
      ...params,
    }));
  }

  const hasIntegrationsGetPermission = useCanGet();
  const hasIntegrationsDiscoveryPermission = useCanListDiscoveryResults();
  const hasPolicyAssignPermission = useCanAssign();

  const mrn = searchParams.get(INTEGRATION_MRN);

  if (!mrn) {
    return <LoadingFailedPage what="AWS Integration" />;
  }

  const clientIntegrationResult = useGetClientIntegrationQuery({
    variables: { mrn },
    pollInterval: 10000,
    fetchPolicy: "network-only",
    skip: !hasIntegrationsGetPermission,
  });

  const listDiscoveryResultsResult = useListDiscoveryResultsQuery({
    variables: { input: { mrn } },
    pollInterval: 10000,
    fetchPolicy: "network-only",
    skip: !hasIntegrationsDiscoveryPermission,
  });

  const [deleteClientIntegration] = useDeleteClientIntegrationMutation({
    refetchQueries: [GetIntegrationsSummaryDocument],
  });

  const [pingClientIntegration] = usePingIntegrationLazyQuery({
    variables: { input: { mrn } },
  });

  const [triggerClientIntegrationScan] = useTriggerActionLazyQuery({
    variables: { input: { mrn, type: ActionType.RunScan } },
    fetchPolicy: "no-cache",
  });

  const integration = clientIntegrationResult.data?.clientIntegration
    .integration as AwsIntegration;

  setDocumentTitle([integration?.name, "AWS", "Integrations", scope.name]);

  if (clientIntegrationResult.loading || listDiscoveryResultsResult.loading) {
    return <LoadingPage what="AWS Integration" />;
  }

  const discoveryResults =
    listDiscoveryResultsResult.data?.listDiscoveryResults;

  if (
    clientIntegrationResult.error ||
    integration?.configurationOptions?.__typename !==
      "HostedAwsConfigurationOptions"
  ) {
    return <LoadingFailedPage what="AWS Integration" />;
  }

  const tabs = ["overview"];
  const tabNames = ["Overview"];

  if (hasPolicyAssignPermission) {
    tabs.push("policies");
    tabNames.push("Recommended Policies");
  }

  const handleTabChange: TabsProps["onChange"] = (e, value) => {
    const currentTab = tabs[value];
    const newUrl = `/space/${SpacePagesIntegrations}/aws/${integrationId}/${currentTab}?${scope.params}&integrationMrn=${mrn}`;
    navigate(newUrl);
  };

  const deleteIntegrationBegin = () => {
    mergeState({ openDeleteConfirmDialog: true });
  };

  const handleDeleteAgentClose = () => {
    mergeState({ isDeleting: false, openDeleteConfirmDialog: false });
  };

  const handleDeleteAgentConfirm = async () => {
    mergeState({ isDeleting: true });
    try {
      await deleteClientIntegration({
        variables: { input: { mrn: integration.mrn } },
      });
      // Display success msg
      enqueueSnackbar("Successfully removed integration.", {
        variant: "success",
      });
      // Nav back to list view
      navigate(`/space/${SpacePagesIntegrations}/aws?${scope.params}`);
    } catch (error) {
      enqueueSnackbar("Failed to remove integration.", { variant: "error" });
    } finally {
      mergeState({ isDeleting: false, openDeleteConfirmDialog: false });
    }
  };

  const triggerScan = async () => {
    try {
      await triggerClientIntegrationScan();
      enqueueSnackbar("Successfully triggered scan.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to trigger scan.", { variant: "error" });
    }
  };

  const ping = async () => {
    try {
      await pingClientIntegration();
      enqueueSnackbar("Successfully pinged AWS.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Failed to ping AWS.", { variant: "error" });
    }
  };

  const discoveredAssets =
    discoveryResults?.stats
      ?.flatMap((asset) => asset ?? [])
      .sort((a, b) => {
        if (a.title < b.title) return -1;
        if (a.title > b.title) return 1;
        return 0;
      }) || [];

  let content = null;
  switch (currentTab) {
    case "overview":
      content = (
        <AWSOverviewAccountTab
          {...{
            scope,
            integration,
            integrationMrn: mrn,
            discoveredAssets,
            integrationStatus: integration.status as AWSIntegrationStatus,
          }}
        />
      );
      break;
    case "policies":
      content = (
        <IntegrationRecommendedPolicies {...{ scope }} filterTypes={["aws"]} />
      );
      break;
  }

  const breadcrumbs = [
    <Link
      key="/space/overview"
      component={RouterLink}
      to={`/space/overview?${scope.params}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Link
      key="/space/integrations"
      component={RouterLink}
      to={`/space/integrations?${scope.params}`}
    >
      Integrations
    </Link>,
    <Link
      key="/space/integrations/aws"
      component={RouterLink}
      to={`/space/integrations/aws?${scope.params}`}
    >
      AWS
    </Link>,
    <Typography key="/space/integrations/aws/detail">
      {integration.name}
    </Typography>,
  ];

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      <Grid
        size={{
          xs: 12,
          sm: 12,
        }}
      >
        <AwsIntegrationHostedHeader
          actions={{
            triggerScan,
            ping,
            deleteIntegration: deleteIntegrationBegin,
          }}
          {...{
            mrn,
            scope,
            awsIntegration: integration,
            integrationId,
          }}
        />
      </Grid>
      <Grid mt={4} mb={3} size={12}>
        <Box display="flex" justifyContent="flex-end">
          <AwsIntegrationAssetsButton
            integrationMrn={integration.mrn}
            show={Boolean(discoveredAssets.length)}
          />
        </Box>
        <Box
          sx={{
            position: "relative",
            mb: 0,
            "&:after": {
              content: "''",
              position: "absolute",
              bottom: 0,
              left: 0,
              width: 1,
              height: 4,
              backgroundColor: "background.light",
            },
          }}
        >
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            value={tabs.indexOf(currentTab)}
            onChange={handleTabChange}
            sx={[
              {
                ".MuiTabs-indicator": {
                  height: 4,
                  zIndex: 1,
                },
              },
            ]}
          >
            {tabs.map((tab, i) => {
              return (
                <Tab
                  key={i}
                  label={tabNames[i]}
                  data-name={`integration-aws-tab-${tabNames[i]}`}
                  sx={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: "text.secondary",
                    textTransform: "capitalize",
                  }}
                />
              );
            })}
          </Tabs>
        </Box>
      </Grid>
      <Grid size={12}>{content}</Grid>
      <ConfirmationDialog
        isOpen={state.openDeleteConfirmDialog}
        onConfirm={handleDeleteAgentConfirm}
        onClose={handleDeleteAgentClose}
        loading={state.isDeleting}
        confirmButtonText="Delete"
        title={`Remove integration "${integration.name}"`}
        content={
          <Typography
            variant="body2"
            sx={{
              mt: -3,
              color: "text.main",
            }}
          >
            Once you delete an integration from Mondoo, all related data will be
            deleted alongside it. You can add the integration again, but any
            historical data will be lost.
          </Typography>
        }
      />
    </Box>
  );
}
